import React, { createContext, useContext, useState, useEffect } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';

const ThemeContext = createContext();

const THEME_KEY = '@amb:theme';

export const ThemeProvider = ({ children }) => {
    // Initialize theme from localStorage or system preference
    const [theme, setTheme] = useState(() => {
        try {
            const savedTheme = localStorage.getItem(THEME_KEY);
            if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
                return savedTheme;
            }

            // Check system preference as fallback
            if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                return 'dark';
            }
        } catch (e) {
            console.warn('Error reading theme from localStorage', e);
        }
        return 'light';
    });

    // Update document and localStorage when theme changes
    useEffect(() => {
        // Apply theme to the root element
        document.documentElement.setAttribute('data-theme', theme);

        // Save to localStorage for persistence
        try {
            localStorage.setItem(THEME_KEY, theme);
        } catch (e) {
            console.warn('Error saving theme to localStorage', e);
        }

        // Update meta theme-color for mobile browsers
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute('content', theme === 'dark' ? '#191919' : '#FFFFFF');
        }
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    // Derived values for skeleton screens
    const skeletonColors = theme === 'dark'
        ? { color: '#2C2C2C', highlightColor: '#383838' }
        : { color: '#ebebeb', highlightColor: '#f5f5f5' };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            <SkeletonTheme color={skeletonColors.color} highlightColor={skeletonColors.highlightColor}>
                {children}
            </SkeletonTheme>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
